'use client'

import { useEffect, useState, type ReactElement } from 'react'
import { Tooltip } from '@base-ui/react/tooltip'
import {
  AlertCircle,
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  CheckCircle2,
  FileDown,
  Flag,
  History,
  HelpCircle,
  Heart,
  Loader2,
  MessageSquare,
  Pencil,
  Pin,
  Reply,
  Send,
  Sparkles,
  Star,
  ThumbsDown,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'
import { UserAvatar } from '@/components/user-avatar'
import {
  ForumAttachmentPicker,
  type ForumAttachmentInput,
} from '@/components/forum-attachment-picker'
import { ForumFilePicker, type ForumFileInput } from '@/components/forum-file-picker'
import { badgeLabel, categoryLabel } from '@/lib/forum-options'

type User = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  disclaimerAccepted: boolean
  isAdmin: boolean
}

type Topic = {
  id: number
  title: string
  body: string
  author_id: number
  created_at: string
  reply_count: number
  author_handle: string
  author_avatar: string | null
  author_callsign: string | null
  author_radios: string | null
  author_badges: string[]
  category: string
  tags: string[]
  solution_post_id: number | null
  is_solved: boolean
  is_pinned: boolean
  is_featured: boolean
  isAuthor: boolean
  isWatching: boolean
  like_count: number
  dislike_count: number
  user_reaction: -1 | 0 | 1
  liked_by?: string[]
  disliked_by?: string[]
  attachment: ForumAttachment | null
  file: ForumFile | null
  poll: ForumPoll | null
}

type Post = {
  id: number
  topic_id: number
  author_id: number
  body: string
  created_at: string
  author_handle: string
  author_avatar: string | null
  author_callsign: string | null
  author_radios: string | null
  author_badges: string[]
  isAuthor: boolean
  attachment: ForumAttachment | null
  file: ForumFile | null
  like_count: number
  dislike_count: number
  user_reaction: -1 | 0 | 1
  liked_by?: string[]
  disliked_by?: string[]
  extra_reactions: Record<string, number>
  extra_reaction_people?: Record<string, string[]>
  user_extra_reactions: string[]
  reply_to_post_id: number | null
  quoted_body: string | null
  quoted_author: string | null
  edited_at: string | null
  isSolution: boolean
}

type ForumAttachment = {
  id: number
  status: 'pending' | 'approved'
  url: string | null
  canReview: boolean
  previewUrl: string | null
}

type ForumFile = {
  id: number
  originalName: string
  kind: string
  sizeBytes: number
  status: 'pending' | 'approved'
  canReview: boolean
  downloadUrl: string | null
}

type ForumPoll = {
  id: number
  question: string
  totalVotes: number
  hasVoted: boolean
  options: Array<{ id: number; label: string; votes: number; selected: boolean }>
}

type PostRevision = {
  id: number
  body: string
  created_at: string
  editor_handle: string
}

const extraReactionOptions = [
  { kind: 'helpful', label: 'Helpful', icon: HelpCircle },
  { kind: 'solved', label: 'Solved', icon: CheckCircle2 },
  { kind: 'useful', label: 'Useful', icon: Sparkles },
] as const

function ReactionPeopleTooltip({
  title,
  count,
  names,
  singular,
  plural,
  privateMessage,
  children,
}: {
  title: string
  count: number
  names: string[]
  singular: string
  plural: string
  privateMessage?: string
  children: ReactElement
}) {
  const visibleNames = [...new Set(names.filter(Boolean))]
  const countText =
    count === 0 ? `No ${plural} yet.` : `${count} ${count === 1 ? singular : plural}`
  const hiddenCount = Math.max(0, count - visibleNames.length)
  const accessibleNames =
    visibleNames.length > 0
      ? `Public names: ${visibleNames.join(', ')}.`
      : privateMessage || 'Names are private.'

  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        render={children}
        delay={300}
        closeDelay={100}
        aria-label={`${title}. ${countText}. ${accessibleNames}`}
      />
      <Tooltip.Portal>
        <Tooltip.Positioner sideOffset={8} className="z-50">
          <Tooltip.Popup className="max-w-72 rounded-lg border border-border bg-card px-3 py-2.5 text-card-foreground shadow-xl">
            <p className="font-semibold">{title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{countText}</p>
            {visibleNames.length > 0 && (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-sm">
                {visibleNames.map((name) => (
                  <li key={name} className="truncate">
                    {name}
                  </li>
                ))}
              </ul>
            )}
            {visibleNames.length > 0 && hiddenCount > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {hiddenCount} {hiddenCount === 1 ? 'name is' : 'names are'} private.
              </p>
            )}
            {count > 0 && visibleNames.length === 0 && privateMessage && (
              <p className="mt-1 text-xs text-muted-foreground">{privateMessage}</p>
            )}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

function formatDateTime(s: string) {
  const date = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function ForumImage({ attachment }: { attachment: ForumAttachment | null }) {
  const [busy, setBusy] = useState(false)
  const [reviewError, setReviewError] = useState('')

  async function review(approve: boolean) {
    if (!attachment) return
    if (!approve && !window.confirm('Reject and permanently delete this image?')) return
    setBusy(true)
    setReviewError('')
    try {
      const response = await fetch(
        `/api/admin/forum/attachments/${attachment.id}${approve ? '/approve' : ''}`,
        { method: approve ? 'POST' : 'DELETE' },
      )
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not update the image.')
      window.location.reload()
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Could not update the image.')
      setBusy(false)
    }
  }

  if (!attachment) return null
  if (attachment.status === 'approved' && attachment.url) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="mt-4 block overflow-hidden rounded-xl border border-border bg-background"
      >
        <img
          src={attachment.url}
          alt="Forum attachment"
          loading="lazy"
          className="max-h-[32rem] w-full object-contain"
        />
      </a>
    )
  }

  if (attachment.canReview && attachment.previewUrl) {
    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-dashed border-primary/40 bg-secondary/40">
        <a href={attachment.previewUrl} target="_blank" rel="noreferrer">
          <img
            src={attachment.previewUrl}
            alt="Pending forum attachment"
            className="max-h-[32rem] w-full bg-background object-contain"
          />
        </a>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
          <p className="text-sm font-medium text-foreground">Pending image approval</p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => review(false)}
              className="text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4" />
              Reject
            </Button>
            <Button size="sm" disabled={busy} onClick={() => review(true)}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Approve
            </Button>
          </div>
        </div>
        {reviewError && (
          <p role="alert" className="border-t border-destructive/20 px-4 py-2 text-sm text-destructive">
            {reviewError}
          </p>
        )}
      </div>
    )
  }

  return (
    <p className="mt-4 rounded-lg border border-dashed border-border bg-background px-3 py-2 text-xs text-muted-foreground">
      Image pending moderator approval.
    </p>
  )
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ForumFileAttachment({ file }: { file: ForumFile | null }) {
  const [busy, setBusy] = useState(false)
  const [reviewError, setReviewError] = useState('')
  if (!file) return null

  async function review(approve: boolean) {
    if (!file || (!approve && !window.confirm('Reject and permanently delete this diagnostic file?'))) {
      return
    }
    setBusy(true)
    setReviewError('')
    try {
      const response = await fetch(
        `/api/admin/forum/files/${file.id}${approve ? '/approve' : ''}`,
        { method: approve ? 'POST' : 'DELETE' },
      )
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not update the file.')
      window.location.reload()
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Could not update the file.')
      setBusy(false)
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <FileDown className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{file.originalName}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {file.kind.toUpperCase()} · {formatFileSize(file.sizeBytes)} ·{' '}
              {file.status === 'approved' ? 'moderator approved' : 'pending review'}
            </p>
          </div>
        </div>
        {file.downloadUrl && (
          <Button
            variant={file.status === 'approved' ? 'outline' : 'ghost'}
            size="sm"
            nativeButton={false}
            render={<a href={file.downloadUrl} />}
          >
            <FileDown className="h-4 w-4" />
            {file.status === 'approved' ? 'Download' : 'Review file'}
          </Button>
        )}
      </div>
      {file.canReview && file.status === 'pending' && (
        <div className="mt-3 flex justify-end gap-2 border-t border-border pt-3">
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => review(false)}
            className="text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
            Reject
          </Button>
          <Button size="sm" disabled={busy} onClick={() => review(true)}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Approve
          </Button>
        </div>
      )}
      {reviewError && <p className="mt-2 text-sm text-destructive">{reviewError}</p>}
    </div>
  )
}

function PollCard({
  poll,
  onVote,
}: {
  poll: ForumPoll | null
  onVote: (optionId: number) => Promise<void>
}) {
  const [busyId, setBusyId] = useState<number | null>(null)
  if (!poll) return null
  return (
    <section className="mt-4 rounded-xl border border-primary/25 bg-secondary/40 p-5">
      <h3 className="font-semibold text-foreground">{poll.question}</h3>
      <div className="mt-4 flex flex-col gap-2">
        {poll.options.map((option) => {
          const percent = poll.totalVotes ? Math.round((option.votes / poll.totalVotes) * 100) : 0
          return (
            <button
              key={option.id}
              type="button"
              disabled={busyId !== null}
              onClick={async () => {
                setBusyId(option.id)
                await onVote(option.id)
                setBusyId(null)
              }}
              className={`relative overflow-hidden rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                option.selected ? 'border-primary bg-card text-primary' : 'border-border bg-card hover:border-primary/40'
              }`}
            >
              <span
                aria-hidden="true"
                className="absolute inset-y-0 left-0 bg-primary/10"
                style={{ width: `${percent}%` }}
              />
              <span className="relative flex items-center justify-between gap-3">
                <span>{option.label}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {option.votes} · {percent}%
                </span>
              </span>
            </button>
          )
        })}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {poll.totalVotes} {poll.totalVotes === 1 ? 'vote' : 'votes'}
        {poll.hasVoted ? ' · Click another option to change your vote.' : ''}
      </p>
    </section>
  )
}

export default function TopicPage() {
  const topicId = window.location.pathname.split('/').pop() || ''
  const [user, setUser] = useState<User | null>(null)
  const [topic, setTopic] = useState<Topic | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loaded, setLoaded] = useState(false)
  const [reply, setReply] = useState('')
  const [attachment, setAttachment] = useState<ForumAttachmentInput | null>(null)
  const [file, setFile] = useState<ForumFileInput | null>(null)
  const [quotedPost, setQuotedPost] = useState<Post | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle')
  const [error, setError] = useState('')
  const [reactingId, setReactingId] = useState<number | null>(null)
  const [topicReacting, setTopicReacting] = useState(false)
  const [reactionError, setReactionError] = useState('')
  const [heartBurst, setHeartBurst] = useState<{ postId: number; key: number } | null>(null)
  const [topicHeartBurst, setTopicHeartBurst] = useState<number | null>(null)
  const [editingPostId, setEditingPostId] = useState<number | null>(null)
  const [editBody, setEditBody] = useState('')
  const [historyPostId, setHistoryPostId] = useState<number | null>(null)
  const [revisions, setRevisions] = useState<PostRevision[]>([])
  const [reportingPostId, setReportingPostId] = useState<number | null>(null)
  const [reportReason, setReportReason] = useState('spam')
  const [reportDetails, setReportDetails] = useState('')
  const [actionNotice, setActionNotice] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [meRes, topicRes] = await Promise.all([fetch('/api/me'), fetch(`/api/forum/topics/${topicId}`)])
        const me = (await meRes.json()) as { user: User | null }
        if (!topicRes.ok) return
        const data = (await topicRes.json()) as { topic: Topic; posts: Post[] }
        if (!cancelled) {
          setUser(me.user)
          setTopic(data.topic)
          setPosts(data.posts)
        }
      } catch {
        // Leave the page blank; the login prompt below will show.
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [topicId])

  useEffect(() => {
    if (!loaded || posts.length === 0 || !window.location.hash) return
    requestAnimationFrame(() => {
      document.querySelector(window.location.hash)?.scrollIntoView({ block: 'start' })
    })
  }, [loaded, posts])

  const inputClass =
    'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30'

  async function submitReply(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setStatus('loading')
    try {
      const res = await fetch(`/api/forum/topics/${topicId}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: reply,
          attachment,
          file,
          replyToPostId: quotedPost?.id ?? null,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) throw new Error(data.error || 'Could not post your reply.')
      window.location.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post your reply.')
      setStatus('error')
    }
  }

  function startQuote(post: Post) {
    setQuotedPost(post)
    setActionNotice('')
    window.setTimeout(() => {
      document.getElementById('reply-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }

  async function deletePost(postId: number) {
    if (!window.confirm('Delete this post?')) return
    const res = await fetch(`/api/forum/posts/${postId}`, { method: 'DELETE' })
    if (res.ok) window.location.reload()
    else {
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      setError(data.error || 'Could not delete the post.')
    }
  }

  async function reactToPost(postId: number, value: 1 | -1) {
    if (reactingId !== null) return
    const post = posts.find((item) => item.id === postId)
    if (!post) return
    const nextValue = post.user_reaction === value ? 0 : value
    const reaction = nextValue === 1 ? 'like' : nextValue === -1 ? 'dislike' : null

    setReactingId(postId)
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/posts/${postId}/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        like_count?: number
        dislike_count?: number
        user_reaction?: -1 | 0 | 1
        liked_by?: string[]
        disliked_by?: string[]
      }
      if (!response.ok) throw new Error(data.error || 'Could not save your reaction.')
      setPosts((current) =>
        current.map((item) =>
          item.id === postId
            ? {
                ...item,
                like_count: data.like_count ?? item.like_count,
                dislike_count: data.dislike_count ?? item.dislike_count,
                user_reaction: data.user_reaction ?? 0,
                liked_by: data.liked_by ?? item.liked_by,
                disliked_by: data.disliked_by ?? item.disliked_by,
              }
            : item,
        ),
      )
      if (nextValue === 1) {
        const burstKey = Date.now()
        setHeartBurst({ postId, key: burstKey })
        window.setTimeout(
          () => setHeartBurst((current) => (current?.key === burstKey ? null : current)),
          1000,
        )
      }
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not save your reaction.')
    } finally {
      setReactingId(null)
    }
  }

  async function reactToTopic(value: 1 | -1) {
    if (!topic || topicReacting) return
    const nextValue = topic.user_reaction === value ? 0 : value
    const reaction = nextValue === 1 ? 'like' : nextValue === -1 ? 'dislike' : null

    setTopicReacting(true)
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/topics/${topic.id}/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        like_count?: number
        dislike_count?: number
        user_reaction?: -1 | 0 | 1
        liked_by?: string[]
        disliked_by?: string[]
      }
      if (!response.ok) throw new Error(data.error || 'Could not save your reaction.')
      setTopic((current) =>
        current
          ? {
              ...current,
              like_count: data.like_count ?? current.like_count,
              dislike_count: data.dislike_count ?? current.dislike_count,
              user_reaction: data.user_reaction ?? 0,
              liked_by: data.liked_by ?? current.liked_by,
              disliked_by: data.disliked_by ?? current.disliked_by,
            }
          : current,
      )
      if (nextValue === 1) {
        const burstKey = Date.now()
        setTopicHeartBurst(burstKey)
        window.setTimeout(
          () => setTopicHeartBurst((current) => (current === burstKey ? null : current)),
          1000,
        )
      }
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not save your reaction.')
    } finally {
      setTopicReacting(false)
    }
  }

  async function extraReaction(postId: number, kind: string) {
    setReactionError('')
    setActionNotice('')
    try {
      const response = await fetch(`/api/forum/posts/${postId}/extra-reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        counts?: Record<string, number>
        selected?: string[]
        people?: Record<string, string[]>
      }
      if (!response.ok) throw new Error(data.error || 'Could not save your reaction.')
      setPosts((current) =>
        current.map((post) =>
          post.id === postId
            ? {
                ...post,
                extra_reactions: data.counts || {},
                extra_reaction_people: data.people || {},
                user_extra_reactions: data.selected || [],
              }
            : post,
        ),
      )
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not save your reaction.')
    }
  }

  async function toggleWatch() {
    if (!topic) return
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/topics/${topic.id}/watch`, { method: 'POST' })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        watching?: boolean
      }
      if (!response.ok) throw new Error(data.error || 'Could not update topic watch.')
      setTopic((current) => (current ? { ...current, isWatching: Boolean(data.watching) } : current))
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not update topic watch.')
    }
  }

  async function toggleTopicFlag(flag: 'pinned' | 'featured') {
    if (!topic) return
    setReactionError('')
    try {
      const field = flag === 'pinned' ? 'is_pinned' : 'is_featured'
      const nextValue = !topic[field]
      const response = await fetch(`/api/admin/topics/${topic.id}/flags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [flag]: nextValue }),
      })
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not update the topic.')
      setTopic((current) => (current ? { ...current, [field]: nextValue } : current))
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not update the topic.')
    }
  }

  async function votePoll(optionId: number) {
    if (!topic) return
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/topics/${topic.id}/poll-vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        poll?: ForumPoll
      }
      if (!response.ok) throw new Error(data.error || 'Could not save your vote.')
      setTopic((current) => (current ? { ...current, poll: data.poll || current.poll } : current))
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not save your vote.')
    }
  }

  async function setSolution(post: Post) {
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/posts/${post.id}/solution`, { method: 'POST' })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        solutionPostId?: number | null
      }
      if (!response.ok) throw new Error(data.error || 'Could not update the solution.')
      setTopic((current) =>
        current
          ? {
              ...current,
              solution_post_id: data.solutionPostId ?? null,
              is_solved: Boolean(data.solutionPostId),
            }
          : current,
      )
      setPosts((current) =>
        current.map((item) => ({ ...item, isSolution: item.id === data.solutionPostId })),
      )
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not update the solution.')
    }
  }

  async function saveEdit(postId: number) {
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/posts/${postId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: editBody }),
      })
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        body?: string
        edited_at?: string
      }
      if (!response.ok) throw new Error(data.error || 'Could not edit the comment.')
      setPosts((current) =>
        current.map((post) =>
          post.id === postId
            ? { ...post, body: data.body || post.body, edited_at: data.edited_at || post.edited_at }
            : post,
        ),
      )
      setEditingPostId(null)
      setActionNotice('Comment updated.')
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not edit the comment.')
    }
  }

  async function showHistory(postId: number) {
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/posts/${postId}/revisions`)
      const data = (await response.json().catch(() => ({}))) as {
        error?: string
        revisions?: PostRevision[]
      }
      if (!response.ok) throw new Error(data.error || 'Could not load revision history.')
      setRevisions(data.revisions || [])
      setHistoryPostId(postId)
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not load revision history.')
    }
  }

  async function reportPost(postId: number) {
    setReactionError('')
    try {
      const response = await fetch(`/api/forum/posts/${postId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reportReason, details: reportDetails }),
      })
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not submit the report.')
      setReportingPostId(null)
      setReportDetails('')
      setActionNotice('Report sent to moderators.')
    } catch (err) {
      setReactionError(err instanceof Error ? err.message : 'Could not submit the report.')
    }
  }

  async function deleteTopic() {
    if (!window.confirm('Delete this topic and all replies?')) return
    const res = await fetch(`/api/forum/topics/${topicId}`, { method: 'DELETE' })
    if (res.ok) window.location.href = '/forum'
    else {
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      setError(data.error || 'Could not delete the topic.')
    }
  }

  if (!loaded) return null

  if (!user) {
    return (
      <ContentPage wide eyebrow="// forums" title="Topic" description="Forum discussion">
        <p className="text-sm text-muted-foreground">
          Please{' '}
          <a href="/login" className="text-primary underline underline-offset-4">
            log in
          </a>{' '}
          to read the forums.
        </p>
      </ContentPage>
    )
  }

  if (!topic) {
    return (
      <ContentPage wide eyebrow="// forums" title="Topic" description="Forum discussion">
        <p className="text-sm text-muted-foreground">This topic could not be found.</p>
      </ContentPage>
    )
  }

  return (
    <ContentPage wide eyebrow="// forums" title="Topic" description={topic.title}>
      <a
        href="/forum"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to forums
      </a>

      <article className="mt-4 rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            {categoryLabel(topic.category)}
          </span>
          {topic.is_pinned && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground">
              <Pin className="h-3 w-3" />
              Pinned
            </span>
          )}
          {topic.is_featured && (
            <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/20 px-2.5 py-1 text-xs text-foreground">
              <Star className="h-3 w-3" />
              Featured
            </span>
          )}
          {topic.is_solved && (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-3 w-3" />
              Solved
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row">
          <UserAvatar name={topic.author_handle} src={topic.author_avatar} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{topic.title}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{topic.author_handle}</span>
                  {topic.author_callsign && (
                    <span className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs text-foreground">
                      {topic.author_callsign}
                    </span>
                  )}
                  <span>{formatDateTime(topic.created_at)}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <Button variant="outline" size="sm" onClick={toggleWatch}>
                  {topic.isWatching ? (
                    <BookmarkCheck className="h-4 w-4" />
                  ) : (
                    <Bookmark className="h-4 w-4" />
                  )}
                  {topic.isWatching ? 'Watching' : 'Watch'}
                </Button>
                {user.isAdmin && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => toggleTopicFlag('pinned')}>
                      <Pin className="h-4 w-4" />
                      {topic.is_pinned ? 'Unpin' : 'Pin'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleTopicFlag('featured')}>
                      <Star className="h-4 w-4" />
                      {topic.is_featured ? 'Unfeature' : 'Feature'}
                    </Button>
                  </>
                )}
                {(topic.isAuthor || user.isAdmin) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Delete topic"
                    onClick={deleteTopic}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {(topic.author_callsign || topic.author_radios || topic.author_badges.length > 0) && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {topic.author_radios && <span>{topic.author_radios}</span>}
                {topic.author_badges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-primary"
                  >
                    {badgeLabel(badge)}
                  </span>
                ))}
              </div>
            )}

            {topic.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {topic.tags.map((tag) => (
                  <a
                    key={tag}
                    href={`/forum?tag=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    #{tag}
                  </a>
                ))}
              </div>
            )}

            <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {topic.body}
            </div>
            <ForumImage attachment={topic.attachment} />
            <ForumFileAttachment file={topic.file} />
            <PollCard poll={topic.poll} onVote={votePoll} />
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
              <Tooltip.Provider delay={300} closeDelay={100}>
                <span className="relative inline-flex">
                  <ReactionPeopleTooltip
                    title="Liked by"
                    count={topic.like_count}
                    names={topic.liked_by ?? []}
                    singular="like"
                    plural="likes"
                    privateMessage="Some members keep their positive reactions private."
                  >
                    <Button
                      variant={topic.user_reaction === 1 ? 'secondary' : 'ghost'}
                      size="sm"
                      disabled={topicReacting}
                      aria-pressed={topic.user_reaction === 1}
                      onClick={() => reactToTopic(1)}
                    >
                      <Heart
                        className="h-4 w-4 text-red-500"
                        fill={topic.user_reaction === 1 ? 'currentColor' : 'none'}
                      />
                      Like original post
                      <span className="font-mono text-xs tabular-nums">{topic.like_count}</span>
                    </Button>
                  </ReactionPeopleTooltip>
                  {topicHeartBurst !== null && (
                    <span key={topicHeartBurst} aria-hidden="true" className="pointer-events-none absolute inset-0">
                      {Array.from({ length: 6 }, (_, index) => (
                        <Heart
                          key={index}
                          className={`like-heart like-heart-${index} h-3.5 w-3.5 fill-current text-red-500`}
                        />
                      ))}
                    </span>
                  )}
                </span>
                <ReactionPeopleTooltip
                  title={user?.isAdmin ? 'Disliked by (moderator view)' : 'Dislikes'}
                  count={topic.dislike_count}
                  names={user?.isAdmin ? topic.disliked_by ?? [] : []}
                  singular="dislike"
                  plural="dislikes"
                  privateMessage="Dislike attribution is private."
                >
                  <Button
                    variant={topic.user_reaction === -1 ? 'secondary' : 'ghost'}
                    size="sm"
                    disabled={topicReacting}
                    aria-pressed={topic.user_reaction === -1}
                    onClick={() => reactToTopic(-1)}
                  >
                    <ThumbsDown className="h-4 w-4" />
                    Dislike
                    <span className="font-mono text-xs tabular-nums">{topic.dislike_count}</span>
                  </Button>
                </ReactionPeopleTooltip>
              </Tooltip.Provider>
            </div>
          </div>
        </div>
      </article>

      <div className="mt-6 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">
          {posts.length} {posts.length === 1 ? 'reply' : 'replies'}
        </h3>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {posts.map((post) => (
          <article
            key={post.id}
            className={`flex flex-col gap-4 rounded-xl border bg-card p-5 sm:flex-row ${
              post.isSolution ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' : 'border-border'
            }`}
          >
            <UserAvatar name={post.author_handle} src={post.author_avatar} size="md" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{post.author_handle}</span>
                    {post.author_callsign && (
                      <span className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs text-foreground">
                        {post.author_callsign}
                      </span>
                    )}
                    <span>{formatDateTime(post.created_at)}</span>
                    {post.edited_at && (
                      <span className="text-xs italic">Edited {formatDateTime(post.edited_at)}</span>
                    )}
                    {post.isSolution && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" />
                        Solution
                      </span>
                    )}
                  </div>
                  {(post.author_radios || post.author_badges.length > 0) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {post.author_radios && <span>{post.author_radios}</span>}
                      {post.author_badges.map((badge) => (
                        <span
                          key={badge}
                          className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-primary"
                        >
                          {badgeLabel(badge)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {(post.isAuthor || user.isAdmin) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Edit post"
                      onClick={() => {
                        setEditingPostId(post.id)
                        setEditBody(post.body)
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {user.isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Show revision history"
                      onClick={() =>
                        historyPostId === post.id
                          ? setHistoryPostId(null)
                          : showHistory(post.id)
                      }
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  )}
                  {(post.isAuthor || user.isAdmin) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Delete post"
                      onClick={() => deletePost(post.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {!post.isAuthor && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-expanded={reportingPostId === post.id}
                      onClick={() =>
                        setReportingPostId((current) => (current === post.id ? null : post.id))
                      }
                    >
                      <Flag className="h-4 w-4" />
                      Report
                    </Button>
                  )}
                </div>
              </div>

              {post.quoted_body && (
                <a
                  href={`#post-${post.reply_to_post_id}`}
                  className="mt-3 block rounded-lg border-l-2 border-primary/50 bg-secondary/50 px-3 py-2 text-sm text-muted-foreground"
                >
                  <span className="block text-xs font-medium text-foreground">
                    {post.quoted_author || 'Community member'} wrote
                  </span>
                  <span className="mt-1 line-clamp-3 block whitespace-pre-wrap">
                    {post.quoted_body}
                  </span>
                </a>
              )}

              {editingPostId === post.id ? (
                <div className="mt-3">
                  <textarea
                    rows={5}
                    minLength={2}
                    maxLength={10000}
                    value={editBody}
                    onChange={(event) => setEditBody(event.target.value)}
                    className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingPostId(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => saveEdit(post.id)}>
                      <CheckCircle2 className="h-4 w-4" />
                      Save changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  id={`post-${post.id}`}
                  className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground"
                >
                  {post.body}
                </div>
              )}
              <ForumImage attachment={post.attachment} />
              <ForumFileAttachment file={post.file} />

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Tooltip.Provider delay={300} closeDelay={100}>
                  <span className="relative inline-flex">
                    <ReactionPeopleTooltip
                      title="Liked by"
                      count={post.like_count ?? 0}
                      names={post.liked_by ?? []}
                      singular="like"
                      plural="likes"
                      privateMessage="Some members keep their positive reactions private."
                    >
                      <Button
                        variant={post.user_reaction === 1 ? 'secondary' : 'ghost'}
                        size="sm"
                        disabled={reactingId === post.id}
                        aria-pressed={post.user_reaction === 1}
                        onClick={() => reactToPost(post.id, 1)}
                      >
                        <Heart
                          className="h-4 w-4 text-red-500"
                          fill={post.user_reaction === 1 ? 'currentColor' : 'none'}
                        />
                        Like
                        <span className="font-mono text-xs tabular-nums">{post.like_count ?? 0}</span>
                      </Button>
                    </ReactionPeopleTooltip>
                    {heartBurst?.postId === post.id && (
                      <span key={heartBurst.key} aria-hidden="true" className="pointer-events-none absolute inset-0">
                        {Array.from({ length: 6 }, (_, index) => (
                          <Heart
                            key={index}
                            className={`like-heart like-heart-${index} h-3.5 w-3.5 fill-current text-red-500`}
                          />
                        ))}
                      </span>
                    )}
                  </span>
                  <ReactionPeopleTooltip
                    title={user?.isAdmin ? 'Disliked by (moderator view)' : 'Dislikes'}
                    count={post.dislike_count ?? 0}
                    names={user?.isAdmin ? post.disliked_by ?? [] : []}
                    singular="dislike"
                    plural="dislikes"
                    privateMessage="Dislike attribution is private."
                  >
                    <Button
                      variant={post.user_reaction === -1 ? 'secondary' : 'ghost'}
                      size="sm"
                      disabled={reactingId === post.id}
                      aria-pressed={post.user_reaction === -1}
                      onClick={() => reactToPost(post.id, -1)}
                    >
                      <ThumbsDown className="h-4 w-4" />
                      Dislike
                      <span className="font-mono text-xs tabular-nums">{post.dislike_count ?? 0}</span>
                    </Button>
                  </ReactionPeopleTooltip>
                  {extraReactionOptions.map(({ kind, label, icon: Icon }) => {
                    const selected = post.user_extra_reactions.includes(kind)
                    return (
                      <ReactionPeopleTooltip
                        key={kind}
                        title={`${label} reactions`}
                        count={post.extra_reactions[kind] || 0}
                        names={post.extra_reaction_people?.[kind] ?? []}
                        singular={`${label} reaction`}
                        plural={`${label} reactions`}
                        privateMessage="Some members keep their positive reactions private."
                      >
                        <Button
                          variant={selected ? 'secondary' : 'ghost'}
                          size="sm"
                          aria-pressed={selected}
                          onClick={() => extraReaction(post.id, kind)}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                          <span className="font-mono text-xs tabular-nums">
                            {post.extra_reactions[kind] || 0}
                          </span>
                        </Button>
                      </ReactionPeopleTooltip>
                    )
                  })}
                </Tooltip.Provider>
                <Button variant="ghost" size="sm" onClick={() => startQuote(post)}>
                  <Reply className="h-4 w-4" />
                  Quote
                </Button>
                {(topic.isAuthor || user.isAdmin) && (
                  <Button
                    variant={post.isSolution ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSolution(post)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {post.isSolution ? 'Unmark solution' : 'Mark solution'}
                  </Button>
                )}
              </div>

              {reportingPostId === post.id && (
                <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <div className="grid gap-2 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
                    <select
                      value={reportReason}
                      onChange={(event) => setReportReason(event.target.value)}
                      className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="spam">Spam</option>
                      <option value="abuse">Abuse or harassment</option>
                      <option value="inappropriate">Inappropriate content</option>
                      <option value="off-topic">Off topic</option>
                      <option value="other">Other</option>
                    </select>
                    <input
                      type="text"
                      maxLength={500}
                      value={reportDetails}
                      onChange={(event) => setReportDetails(event.target.value)}
                      placeholder="Optional details for the moderators"
                      className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="mt-2 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setReportingPostId(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => reportPost(post.id)}>
                      <Send className="h-4 w-4" />
                      Send report
                    </Button>
                  </div>
                </div>
              )}

              {user.isAdmin && historyPostId === post.id && (
                <div className="mt-3 rounded-lg border border-border bg-background p-3">
                  <p className="text-sm font-medium">Revision history</p>
                  {revisions.length === 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground">No earlier revisions.</p>
                  ) : (
                    <div className="mt-2 flex flex-col gap-2">
                      {revisions.map((revision) => (
                        <div key={revision.id} className="rounded-md border border-border p-2">
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(revision.created_at)} by {revision.editor_handle}
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-sm">{revision.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      {reactionError && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {reactionError}
        </p>
      )}
      {actionNotice && (
        <p role="status" className="mt-3 text-sm text-primary">
          {actionNotice}
        </p>
      )}

      {user.disclaimerAccepted ? (
        <form
          id="reply-form"
          onSubmit={submitReply}
          className="mt-6 scroll-mt-24 rounded-2xl border border-border bg-card p-6"
        >
          <h3 className="flex items-center gap-2 font-semibold">
            <Reply className="h-4 w-4 text-primary" />
            Reply
          </h3>
          {quotedPost && (
            <div className="mt-3 rounded-lg border-l-2 border-primary/50 bg-secondary/50 px-3 py-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground">
                    Replying to {quotedPost.author_handle}
                  </p>
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
                    {quotedPost.body}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label="Cancel quoted reply"
                  onClick={() => setQuotedPost(null)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
          <textarea
            required
            rows={4}
            minLength={2}
            maxLength={10000}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write your reply…"
            className="mt-3 w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
          <div className="mt-3 flex flex-col gap-1.5">
            <span className="text-sm font-medium">Image attachment</span>
            <ForumAttachmentPicker
              value={attachment}
              onChange={setAttachment}
              disabled={status === 'loading'}
            />
          </div>
          <div className="mt-3 flex flex-col gap-1.5">
            <span className="text-sm font-medium">Diagnostic file</span>
            <ForumFilePicker
              value={file}
              onChange={setFile}
              disabled={status === 'loading'}
            />
          </div>
          {status === 'error' && error && (
            <div
              role="alert"
              className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Reply className="h-4 w-4" />
              )}
              Post reply
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">
          Accept the{' '}
          <a href="/forum" className="text-primary underline underline-offset-4">
            forum rules
          </a>{' '}
          before replying.
        </p>
      )}
    </ContentPage>
  )
}
